import type { Messages, NamespaceKeys, NestedKeyOf, RichTranslationValues } from "next-intl";
// eslint-disable-next-line no-restricted-imports -- in this file we implement our own useTranslations hook
import { useTranslations as useNextIntlTranslations } from "next-intl";
import type { ComponentType, ReactNode } from "react";
import { createElement } from "react";

import { MigrationLink } from "@/common/components-2/migration-link";
import { isString } from "@/common/helpers/lang";

const elementsMap: RichTranslationValues = {
  ...htmlTagToReactNode("a", MigrationLink),
  ...htmlTagToReactNode("br"),
  ...htmlTagToReactNode("em"),
  ...htmlTagToReactNode("li"),
  ...htmlTagToReactNode("span"),
  ...htmlTagToReactNode("strong"),
  ...htmlTagToReactNode("ul"),
};

export function useTranslations<NestedKey extends NamespaceKeys<Messages, NestedKeyOf<Messages>> = never>(
  namespace?: NestedKey,
) {
  const t = useNextIntlTranslations(namespace);

  // eslint-disable-next-line @typescript-eslint/unbound-method -- it doesn't use this
  const originalRich = t.rich;

  // eslint-disable-next-line @typescript-eslint/promise-function-async -- it's not a promise
  t.rich = (key, ...args) => {
    const [values, ...restArgs] = args as unknown as [object | undefined, ...unknown[]];
    const rest = [values ? { ...elementsMap, ...values } : elementsMap, ...restArgs] as unknown as typeof args;

    return originalRich(key, ...rest);
  };

  return t;
}

// biome-ignore lint/suspicious/noExplicitAny: we cannot validate props of translated elements
function htmlTagToReactNode(htmlTag: string, element: string | ComponentType<any> = htmlTag): RichTranslationValues {
  return {
    [htmlTag]: (chunks: ReactNode) => {
      if (!Array.isArray(chunks)) return createElement(element, {}, chunks);
      if (chunks.length === 0) return createElement(element, {});

      const [firstChunk, ...restChunks] = chunks as ReactNode[];

      if (!isString(firstChunk) || !firstChunk.startsWith("|")) {
        return createElement(element, {}, chunks);
      }

      const propSeparatorEndIndex = firstChunk.slice(1).indexOf("|") + 1;

      if (propSeparatorEndIndex === 0) {
        return createElement(element, {}, chunks);
      }

      const propString = firstChunk.slice(1, propSeparatorEndIndex);
      const value = firstChunk.slice(propSeparatorEndIndex + 1);

      const props = Object.fromEntries(
        propString.split('" ').map((prop) => prop.replaceAll('"', "").split("=") as [string, string]),
      );

      return createElement(element, props, value, ...restChunks);
    },
  };
}
